import React from 'react';
import {
  FIELD_MAP_MAIN,
  FIELD_MAP_TRANSLATION,
  ModuleViewProps,
  getUiLabel,
} from './ModuleViewUtil';
import { OptionalLink } from '@designsystem/stories/01-core/OptionalLink';
import { Info, InfoItemProps } from '@designsystem/stories/02-atoms/Info';
import { normalizeLink } from '@fhnw/helpers/DesignSystem/normalizeContent';

const ModuleView: React.FC<ModuleViewProps> = ({ content }) => {
  const queryLang =
    typeof window !== 'undefined'
      ? new URLSearchParams(window.location.search).get('show_language')
      : null;

  const mainContentLang = content.hauptsprache?.toLowerCase() || 'de';
  const currentLang = queryLang || mainContentLang;
  const getTranslatedFieldValue = (
    genericFieldName: string,
  ): string | null | undefined => {
    const useTranslationMap =
      currentLang !== mainContentLang &&
      currentLang === (content.uebersetzung?.toLowerCase() || null);
    const mapToUse = useTranslationMap ? FIELD_MAP_TRANSLATION : FIELD_MAP_MAIN;

    const actualFieldName = mapToUse[genericFieldName];

    if (actualFieldName && content.hasOwnProperty(actualFieldName)) {
      return content[actualFieldName];
    } else if (
      // Fallback to main language field if specific language field is missing
      FIELD_MAP_MAIN[genericFieldName] &&
      content.hasOwnProperty(FIELD_MAP_MAIN[genericFieldName])
    ) {
      return content[FIELD_MAP_MAIN[genericFieldName]];
    }
    return undefined;
  };

  const formatSpecialNummer = (
    nummer: string | null | undefined,
  ): string | null | undefined => {
    if (!nummer || typeof nummer !== 'string') return nummer;
    const lastDashIndex = nummer.lastIndexOf('-');
    const lastDotIndex = nummer.lastIndexOf('.');
    if (
      lastDashIndex !== -1 &&
      lastDotIndex !== -1 &&
      lastDotIndex > lastDashIndex + 1
    ) {
      return nummer.slice(lastDashIndex + 2, lastDotIndex);
    }
    return '';
  };

  const translationLang = content.uebersetzung?.toLowerCase() || null;

  const normalizeStringForLabelLookup = (
    str: string | null | undefined,
  ): string => {
    return str ? str.toLowerCase().replace(/\s+/g, '-') : '';
  };

  const items: InfoItemProps[] = [
    {
      key: getUiLabel('number', currentLang),
      value: formatSpecialNummer(content.Nummer),
    },
    {
      key: getUiLabel('leading', currentLang),
      value: getTranslatedFieldValue('LeitungText'),
      structured: true,
    },
    {
      key: getUiLabel('ects', currentLang),
      value: getTranslatedFieldValue('ECTS'),
    },
  ];

  if (content.Unterrichtssprache) {
    items.push({
      key:
        getUiLabel('teachingLanguage', currentLang) ||
        content.Unterrichtssprache,
      value:
        getUiLabel(
          normalizeStringForLabelLookup(content.Unterrichtssprache),
          currentLang,
        ) || content.Unterrichtssprache,
    });
  }

  function appendLabelMemoRange(
    items: InfoItemProps[],
    start: number,
    end: number,
    getter: (key: string) => string | null | undefined,
  ) {
    for (let i = start; i <= end; i++) {
      const label = getter(`Label${i}`);
      const memo = getter(`Memo${i}`);

      if (label && memo) {
        items.push({
          key: label,
          value: memo,
          structured: true,
        });
      }
    }
  }

  appendLabelMemoRange(items, 1, 13, getTranslatedFieldValue);

  const labelValue = getTranslatedFieldValue('LabelBemerk');
  const memoValue = getTranslatedFieldValue('MemoBemerk');
  if (
    labelValue &&
    memoValue !== null &&
    memoValue !== undefined &&
    memoValue !== ''
  ) {
    items.push({
      key: String(labelValue),
      value: memoValue,
      structured: true,
    });
  }

  if (currentLang === 'en' && content.LabelE14 && content.MemoE14) {
    items.push({
      key: String(content.LabelE14),
      value: content.MemoE14,
      structured: true,
    });
  }

  return (
    <div className="page__section">
      <div className="page__section-content">
        {translationLang && (
          <p className="language-switch">
            <OptionalLink
              href={`${normalizeLink(content['@id'])}?show_language=${
                currentLang === mainContentLang
                  ? translationLang
                  : mainContentLang
              }`}
            >
              {currentLang === mainContentLang
                ? getUiLabel('switchToEnglish', currentLang)
                : getUiLabel('switchToGerman', currentLang)}
            </OptionalLink>
          </p>
        )}

        <Info type="extended" items={items} />
      </div>
    </div>
  );
};

export default ModuleView;
